/**
 * Yara's crop-calibrated layers, in the order they follow Yara's NDVI.
 *
 * Asked for by the AllFarm users: Biomass and N-Uptake belong with the Yara
 * NDVI layers they come from, not at the bottom of the list where their
 * display_order put them (the next free numbers when they were added).
 *
 * Done here rather than by renumbering analysis_definition.display_order: the
 * sequence screens send displayOrder to the API as the analysis definition id
 * (maps-sequence-view.component.ts, analysis-sequence.component.ts), so
 * renumbering would point them at the wrong type.
 */
const FOLLOW_YARA_NDVI = ['S1 SATELLITE', 'SN SATELLITE'];

const YARA_NDVI_PREFIX = 'YARA NDVI SATELLITE';

/**
 * The types in their given order, except that Biomass and N-Uptake move to
 * directly after the last Yara NDVI type.
 *
 * Returns a new array. A list with no Yara NDVI type, which is every tenant
 * but AllFarm, comes back in its original order.
 */
export function withYaraLayersTogether<T extends { type: string }>(types: T[] | null | undefined): T[] {
  const list = [...(types || [])];
  const name = (t: T) => (t?.type || '').toUpperCase();

  const lastYara = list.map(name).reduce((last, n, i) => n.startsWith(YARA_NDVI_PREFIX) ? i : last, -1);
  if (lastYara < 0) {
    return list;
  }

  const moving = FOLLOW_YARA_NDVI
    .map(match => list.find(t => name(t) === match))
    .filter((t): t is T => !!t);
  const rest = list.filter(t => !moving.includes(t));
  const insertAt = rest.indexOf(list[lastYara]) + 1;

  return [...rest.slice(0, insertAt), ...moving, ...rest.slice(insertAt)];
}
